import { Check, Columns3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import type { ColumnDefinition } from "@/stores/createColumnVisibilityStore";

interface ColumnSettingsMenuProps<TId extends string> {
  /** Every column of the table, in the order they should be listed. */
  columns: readonly ColumnDefinition<TId>[];

  /** From the table's column store. */
  isVisible: (id: TId) => boolean;
  toggle: (id: TId) => void;
  showAll: () => void;

  /** How many are hidden — only drives whether "Show all columns" is offered. */
  hiddenCount: number;

  /** Extra classes for the trigger, so a toolbar can size it like its neighbours. */
  className?: string;
}

/**
 * The "which columns do I want to see" control for an admin table.
 *
 * Pairs with `createColumnVisibilityStore`: this renders the menu, the store owns the state and
 * the rules. Every admin list can drop it next to its Filters button and pass its own store's
 * three functions — nothing here is specific to one table.
 *
 * Locked columns are listed but disabled rather than omitted. A menu that silently leaves out
 * three columns reads as a bug in the menu; one that shows them greyed out reads as a decision.
 */
export function ColumnSettingsMenu<TId extends string>({
  columns,
  isVisible,
  toggle,
  showAll,
  hiddenCount,
  className,
}: ColumnSettingsMenuProps<TId>) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          // Icon only, so the accessible name comes from here. No count of hidden columns, on
          // the button or in its label: a table can ship with an optional column hidden by
          // default (the payments list does), and the badge then sat there permanently reading
          // as an alert about something nobody had done. The menu itself is where the operator
          // sees what is off.
          title="Columns"
          aria-label="Columns"
          className={className}
        >
          <Columns3 className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Columns</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {columns.map((column) => {
          const visible = isVisible(column.id);

          return (
            <DropdownMenuCheckboxItem
              key={column.id}
              checked={visible}
              disabled={column.locked}
              // Radix closes the menu when an item is chosen. Hiding four columns would mean
              // reopening the menu four times, so the close is suppressed and the menu stays put
              // until it is dismissed.
              onSelect={(event) => event.preventDefault()}
              onCheckedChange={() => toggle(column.id)}
              // A bare tick in the gutter says "this one is on" but gives nothing to aim at, so
              // the row reads as a label rather than a control. `pl-2` reclaims the gutter the
              // shared item reserves for that tick and `[&>span:first-child]:hidden` drops the
              // tick itself; the box below takes its place.
              className="cursor-pointer gap-2 pl-2 [&>span:first-child]:hidden"
            >
              {/* Drawn, not a real <Checkbox>: the menu item already carries the click handler,
                  role="menuitemcheckbox" and aria-checked, so nesting an interactive Radix
                  checkbox inside it would put two controls where the operator sees one. Styling
                  mirrors components/ui/checkbox so it reads as the same control as the row boxes
                  in the table. */}
              <span
                aria-hidden="true"
                className={cn(
                  "flex size-4 shrink-0 items-center justify-center rounded-[4px] border shadow-sm transition-colors",
                  visible
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-input bg-background",
                )}
              >
                {visible && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
              </span>
              {column.label}
            </DropdownMenuCheckboxItem>
          );
        })}

        {hiddenCount > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => showAll()}
              className="cursor-pointer"
            >
              Show all columns
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
